import moment from 'moment';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';

export const purchaseRequestApi = createApi({
  tagTypes: [
    'PURCHASE_REQUEST_LIST',
    'PURCHASE_REQUEST_TAGS',
    'PURCHASE_REQUEST_ITEM_LIST',
    'PURCHASE_REQUEST',
    'PURCHASE_REQUEST_ITEM',
  ],

  reducerPath: 'purchaseRequestApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    purchaseRequestList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters, forDashboard } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };

        if (forDashboard) {
          params.forDashboard = true;
          params.assignment = JSON.parse(localStorage.getItem('userId') || '');
        }

        return {
          url: `/wrd/api/purchase-request/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content[0] || {};
        const { purchaseManagers = {}, purchaseRequestResponses = [] } = content;

        if (purchaseRequestResponses.length) {
          content.purchaseRequestResponses = purchaseRequestResponses.map((item: any) => ({
            ...item,
            purchaseManager: purchaseManagers[item.purchaseManagerId],
            formattedLastModifiedDate: {
              full: moment(item.lastModifyDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.lastModifyDate).format('DD.MM.YYYY'),
            },
            formattedCreatedDate: {
              full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.createDate).format('DD.MM.YYYY'),
            },
          }));
        }
        return response.data;
      },
      providesTags: () => ['PURCHASE_REQUEST_LIST'],
    }),
    purchaseRequestById: builder.query<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/purchase-request/get-by-id?purchaseRequestId=${id}`,
        };
      },
      transformResponse: (response: any) => {
        const { managers = {}, purchaseRequest = {} } = response?.data || {};
        return {
          ...response.data,
          assignments: {
            purchaseManager: managers[purchaseRequest.purchaseManagerId],
            purchaseManagerAssistant: managers[purchaseRequest.purchaseManagerAssistantId],
            salesManager: managers[purchaseRequest.salesManagerId],
            salesManagerAssistant: managers[purchaseRequest.salesManagerAssistantId],
          },
          purchaseRequest: {
            ...purchaseRequest,
            isFinalStatus: purchaseRequest?.status === 'SOURCED' || purchaseRequest?.status === 'PRICE_CONFIRMED',
            formattedLastModifiedDate: moment(purchaseRequest.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
            formattedCreatedDate: moment(purchaseRequest.createDate).format('DD.MM.YYYY HH:mm:ss'),
          },
        };
      },
      providesTags: () => ['PURCHASE_REQUEST'],
    }),
    purchaseRequestStatusChange: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-request/status',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST'],
    }),
    updatePurchaseRequestManagement: builder.mutation<any, any>({
      query: (data) => ({
        url: '/wrd/api/purchase-request/assignment',
        method: 'put',
        body: data,
      }),
      invalidatesTags: () => ['PURCHASE_REQUEST', 'PURCHASE_REQUEST_LIST'],
    }),
    purchaseRequestItems: builder.query<any, any>({
      query: (args) => {
        const { purchaseRequestId } = args || {};
        const queryParams = purchaseRequestId ? `?purchaseRequestId=${purchaseRequestId}` : '';
        return {
          url: `/wrd/api/purchase-request/item${queryParams}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['PURCHASE_REQUEST_ITEM'],
    }),
    purchaseRequestItemAdd: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/purchase-request/item`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_ITEM'],
    }),
    requestPrice: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/request-price`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_ITEM'],
    }),
    purchaseRequestItemUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/wrd/api/purchase-request/item`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_ITEM'],
    }),
    purchaseRequestItemDelete: builder.mutation<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/wrd/api/purchase-request/item?id=${id}`,
          method: 'delete',
        };
      },
      invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_ITEM'],
    }),
    purchaseRequestEmailTemplate: builder.query<any, any>({
      query: (args) => {
        const { languageId } = args;
        return {
          url: `/wrd/api/email-body-template/${languageId}`,
          method: 'get',
        };
      },
    }),
    purchaseRequestDeals: builder.query<any, any>({
      query: (args) => {
        const { dealId } = args;
        return {
          url: `/wrd/api/purchase-request/deal/${dealId}`,
          method: 'get',
        };
      },
    }),
    // itemTags: builder.query<any, void>({
    //   query: () => ({
    //     url: '/item/api/item-management/get-tags',
    //   }),
    //   transformResponse: (response: any) => {
    //     // Transform the response to the desired shape
    //     return response?.data || [];
    //   },
    //   providesTags: () => ['PURCHASE_REQUEST_TAGS'],
    // }),
    // itemCreate: builder.mutation<any, any>({
    //   query: (body) => {
    //     return {
    //       url: `/item/api/item-management`,
    //       method: 'post',
    //       body,
    //     };
    //   },
    //   invalidatesTags: () => ['PURCHASE_REQUEST_LIST'],
    // }),
    // itemUpdate: builder.mutation<any, any>({
    //   query: (body) => {
    //     return {
    //       url: `/item/api/item-management`,
    //       method: 'put',
    //       body,
    //     };
    //   },
    //   invalidatesTags: () => ['PURCHASE_REQUEST_LIST', 'PURCHASE_REQUEST', 'PURCHASE_REQUEST_SUPPLIER_LIST'],
    // }),
  }),
});
export const {
  usePurchaseRequestByIdQuery,
  usePurchaseRequestListQuery,
  usePurchaseRequestStatusChangeMutation,
  useUpdatePurchaseRequestManagementMutation,
  usePurchaseRequestItemsQuery,
  usePurchaseRequestItemUpdateMutation,
  usePurchaseRequestItemAddMutation,
  usePurchaseRequestItemDeleteMutation,
  usePurchaseRequestEmailTemplateQuery,
  useLazyPurchaseRequestEmailTemplateQuery,
  useRequestPriceMutation,
  usePurchaseRequestDealsQuery,
} = purchaseRequestApi;
